import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";

// ── Helpers ──────────────────────────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

const ACCENT = "#7f5af0";
const ACCENT2 = "#2cb67d";
const DARK = "#0a0a0f";
const CARD_BG = "#13131a";
const BORDER = "rgba(127,90,240,0.18)";
const TEXT = "#e2e8f0";
const MUTED = "#94a3b8";

// ── Animated counter ─────────────────────────────────────────────────────────
const Counter = ({ end, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(end / 60);
    const t = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(t); }
      else setCount(start);
    }, 24);
    return () => clearInterval(t);
  }, [inView, end]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

// ── Glowing divider ───────────────────────────────────────────────────────────
const GlowLine = () => (
  <div style={{ height: "1px", background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`, margin: "0 auto", maxWidth: "600px", opacity: 0.5 }} />
);

// ── Section wrapper ───────────────────────────────────────────────────────────
const Section = ({ children, style = {} }) => (
  <section style={{ padding: "100px 24px", position: "relative", ...style }}>
    <div style={{ maxWidth: "1100px", margin: "0 auto" }}>{children}</div>
  </section>
);

const Tag = ({ children }) => (
  <div style={{
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: `${ACCENT}18`, border: `1px solid ${ACCENT}44`,
    color: ACCENT, padding: "5px 14px", borderRadius: "20px",
    fontSize: "11px", fontFamily: "'Orbitron', monospace",
    letterSpacing: "2px", textTransform: "uppercase", marginBottom: "20px",
  }}>
    {children}
  </div>
);

const H2 = ({ children, style = {} }) => (
  <h2 style={{
    fontFamily: "'Orbitron', monospace", fontSize: "clamp(26px, 4vw, 40px)",
    fontWeight: "900", letterSpacing: "2px", textTransform: "uppercase",
    color: "#ffffff", lineHeight: "1.2", margin: "0 0 16px",
    ...style,
  }}>{children}</h2>
);

// ── Main Component ────────────────────────────────────────────────────────────
const LandingPage = ({ requestAuth }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleCTA = () => {
    const dest = requestAuth ? requestAuth("/app") : "/app";
    if (dest) navigate(dest);
  };

  return (
    <div style={{ background: DARK, color: TEXT, fontFamily: "'Rajdhani', 'Segoe UI', sans-serif", minHeight: "100vh", overflowX: "hidden" }}>

      {/* ─── NAV ─────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: "rgba(10,10,15,0.85)", backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${BORDER}`,
        padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: "64px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img src="/Scrobits_logo_black.png" alt="Scrobits" style={{ height: "26px", filter: "invert(1)", opacity: 0.9 }} />
          <span style={{ fontFamily: "'Orbitron', monospace", color: ACCENT, fontSize: "14px", fontWeight: "700", letterSpacing: "3px" }}>HEROSCAN</span>
        </div>
        <div style={{ display: "flex", gap: "32px", alignItems: "center" }}>
          {["How It Works", "Features", "Profiles", "Events"].map((item) => (
            <a key={item} href={`#${item.toLowerCase().replace(/\s/g, "-")}`}
              style={{ color: MUTED, fontSize: "13px", textDecoration: "none", letterSpacing: "0.5px", transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = "#fff"}
              onMouseLeave={e => e.target.style.color = MUTED}
            >{item}</a>
          ))}
          <button
            onClick={handleCTA}
            style={{
              background: ACCENT, color: "#fff", border: "none",
              padding: "9px 22px", borderRadius: "6px", cursor: "pointer",
              fontFamily: "'Orbitron', monospace", fontSize: "11px",
              fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase",
            }}
          >Try Free</button>
        </div>
      </nav>

      {/* ─── HERO ────────────────────────────────────────────────────────── */}
      <section style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "120px 24px 80px", position: "relative", overflow: "hidden" }}>
        {/* Background glow orbs */}
        <div style={{ position: "absolute", top: "20%", left: "10%", width: "500px", height: "500px", background: `radial-gradient(circle, ${ACCENT}20 0%, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "10%", right: "5%", width: "400px", height: "400px", background: `radial-gradient(circle, ${ACCENT2}15 0%, transparent 70%)`, pointerEvents: "none" }} />
        {/* Grid lines */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${BORDER} 1px, transparent 1px), linear-gradient(90deg, ${BORDER} 1px, transparent 1px)`, backgroundSize: "60px 60px", opacity: 0.3, pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 2, maxWidth: "860px" }}>
          <motion.div {...fadeUp(0)}>
            <Tag>⚡ Powered by Anthropic Claude + OpenAI</Tag>
          </motion.div>

          <motion.h1 {...fadeUp(0.1)} style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: "clamp(38px, 7vw, 80px)",
            fontWeight: "900", letterSpacing: "4px",
            textTransform: "uppercase", lineHeight: "1.05",
            margin: "0 0 24px",
            background: `linear-gradient(135deg, #ffffff 30%, ${ACCENT} 70%)`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            Discover Your<br />Inner Hero
          </motion.h1>

          <motion.p {...fadeUp(0.2)} style={{ fontSize: "clamp(16px, 2vw, 20px)", color: MUTED, lineHeight: "1.7", marginBottom: "40px", maxWidth: "620px", margin: "0 auto 40px" }}>
            HeroScan is an AI-powered personality engine that transforms who you are into a high-fidelity superhero identity — complete with a cinematic portrait where your face is preserved.
          </motion.p>

          <motion.div {...fadeUp(0.3)} style={{ display: "flex", gap: "16px", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={handleCTA}
              style={{
                background: `linear-gradient(135deg, ${ACCENT}, #6644cc)`,
                color: "#fff", border: "none",
                padding: "16px 40px", borderRadius: "8px", cursor: "pointer",
                fontFamily: "'Orbitron', monospace", fontSize: "13px",
                fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase",
                boxShadow: `0 0 32px ${ACCENT}50`,
              }}
            >Begin Your Scan →</button>
            <a href="#how-it-works" style={{
              background: "transparent", color: TEXT,
              border: `1.5px solid ${BORDER}`,
              padding: "16px 32px", borderRadius: "8px", cursor: "pointer",
              fontFamily: "'Orbitron', monospace", fontSize: "12px",
              fontWeight: "600", letterSpacing: "2px", textDecoration: "none",
              display: "inline-flex", alignItems: "center",
            }}>See How It Works</a>
          </motion.div>

          {/* Stats bar */}
          <motion.div {...fadeUp(0.4)} style={{ display: "flex", gap: "48px", justifyContent: "center", marginTop: "72px", flexWrap: "wrap" }}>
            {[
              { n: 500, suffix: "+", label: "Heroes Created" },
              { n: 20, suffix: "", label: "Unique Hero Archetypes" },
              { n: 98, suffix: "%", label: "Accuracy Score" },
            ].map(({ n, suffix, label }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "clamp(28px, 4vw, 40px)", fontWeight: "900", color: ACCENT }}>
                  <Counter end={n} suffix={suffix} />
                </div>
                <div style={{ fontSize: "11px", color: MUTED, letterSpacing: "1.5px", textTransform: "uppercase", marginTop: "4px" }}>{label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <GlowLine />

      {/* ─── HOW IT WORKS ────────────────────────────────────────────────── */}
      <Section id="how-it-works">
        <motion.div {...fadeUp(0)} style={{ textAlign: "center", marginBottom: "64px" }}>
          <Tag>⚙ The Process</Tag>
          <H2>How It Works</H2>
          <p style={{ color: MUTED, fontSize: "16px", maxWidth: "520px", margin: "0 auto", lineHeight: "1.7" }}>
            From your first click to your superhero portrait — the entire experience takes under 3 minutes.
          </p>
        </motion.div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "2px", position: "relative" }}>
          {/* Connector line */}
          <div style={{ position: "absolute", top: "52px", left: "10%", right: "10%", height: "2px", background: `linear-gradient(90deg, transparent, ${ACCENT}60, transparent)`, pointerEvents: "none" }} />

          {[
            { step: "01", icon: "📋", title: "Fill Your Profile", desc: "Name, profession, birth date — the foundation of your identity scan." },
            { step: "02", icon: "📸", title: "Capture Your Face", desc: "Webcam snapshot or upload. Your face becomes the canvas for your hero portrait." },
            { step: "03", icon: "🧠", title: "Answer Questions", desc: "A short set of personality prompts that reveal how you think and lead." },
            { step: "04", icon: "⚡", title: "AI Analysis", desc: "Claude analyzes your data, numerology, and patterns to forge your superhero identity." },
            { step: "05", icon: "🦸", title: "Receive Your Hero", desc: "Get your full profile — title, powers, archetype — plus a cinematic avatar." },
          ].map(({ step, icon, title, desc }, i) => (
            <motion.div key={step} {...fadeUp(i * 0.1)} style={{
              background: CARD_BG, border: `1px solid ${BORDER}`,
              borderRadius: "12px", padding: "28px 20px",
              textAlign: "center", position: "relative", zIndex: 2,
            }}>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "10px", color: ACCENT, letterSpacing: "2px", marginBottom: "14px" }}>{step}</div>
              <div style={{ fontSize: "32px", marginBottom: "12px" }}>{icon}</div>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "12px", fontWeight: "700", color: "#fff", letterSpacing: "1px", marginBottom: "10px", textTransform: "uppercase" }}>{title}</div>
              <div style={{ fontSize: "13px", color: MUTED, lineHeight: "1.6" }}>{desc}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      <GlowLine />

      {/* ─── FEATURES ────────────────────────────────────────────────────── */}
      <Section id="features" style={{ background: `${CARD_BG}80` }}>
        <motion.div {...fadeUp(0)} style={{ textAlign: "center", marginBottom: "64px" }}>
          <Tag>🔬 Capabilities</Tag>
          <H2>Built with the Best AI</H2>
          <p style={{ color: MUTED, fontSize: "16px", maxWidth: "520px", margin: "0 auto", lineHeight: "1.7" }}>
            We combine two frontier AI models to deliver an experience that is both deeply personal and visually extraordinary.
          </p>
        </motion.div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
          {[
            {
              model: "Claude Sonnet (Anthropic)",
              badge: "PERSONALITY ENGINE",
              color: ACCENT,
              icon: "🧬",
              points: [
                "Analyzes name, profession, birth date, and personality answers",
                "Calculates full numerology — Mulank, Expression, Soul Urge numbers",
                "Generates your Superhero Title, Archetype, Superpower & Kryptonite",
                "Produces a unique one-liner that captures your essence",
                "Assigns a personal color scheme and visual identity",
              ],
            },
            {
              model: "GPT-4o (OpenAI)",
              badge: "AVATAR GENERATOR",
              color: ACCENT2,
              icon: "🎨",
              points: [
                "Receives your actual photo as input — not just a text description",
                "Preserves your exact facial structure, skin tone, and jawline",
                "Renders you in a cinematic 8K superhero art style",
                "Uses trademark-safe visual descriptions to bypass content filters",
                "Outputs a stunning portrait that looks like you as a hero",
              ],
            },
          ].map(({ model, badge, color, icon, points }) => (
            <motion.div key={badge} {...fadeUp(0.1)} style={{
              background: CARD_BG, border: `1.5px solid ${color}40`,
              borderRadius: "16px", padding: "36px 32px",
              boxShadow: `0 0 40px ${color}10`,
            }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: `${color}18`, border: `1px solid ${color}44`, color, padding: "4px 12px", borderRadius: "4px", fontSize: "9px", fontFamily: "'Orbitron', monospace", letterSpacing: "2px", marginBottom: "20px" }}>
                {badge}
              </div>
              <div style={{ fontSize: "40px", marginBottom: "12px" }}>{icon}</div>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "14px", fontWeight: "700", color: "#fff", marginBottom: "6px" }}>{model}</div>
              <ul style={{ margin: "20px 0 0", padding: 0, listStyle: "none" }}>
                {points.map((p, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "12px", fontSize: "14px", color: MUTED, lineHeight: "1.6" }}>
                    <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: color, flexShrink: 0, marginTop: "8px" }} />{p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </Section>

      <GlowLine />

      {/* ─── PROFILE BUILDING ────────────────────────────────────────────── */}
      <Section id="profiles">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "64px", alignItems: "center" }}>
          <motion.div {...fadeUp(0)}>
            <Tag>🏆 What Sets Us Apart</Tag>
            <H2>Profiles Built<br />to the Finest Detail</H2>
            <p style={{ color: MUTED, fontSize: "15px", lineHeight: "1.8", marginBottom: "32px" }}>
              We don't just generate a fun nickname. HeroScan constructs a multi-layered identity using the science of numerology, behavioral psychology, and generative AI — creating a profile that genuinely reflects who you are.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {[
                { icon: "🔢", title: "Numerology Engine", desc: "Birth date decoded into Mulank, Expression Number, and Soul Urge — each revealing a different dimension of your personality." },
                { icon: "🎯", title: "Archetype Mapping", desc: "20 distinct hero archetypes — from The Strategist to The Empath — matched to your specific behavioral pattern." },
                { icon: "⚡", title: "Superpower + Kryptonite", desc: "Your greatest strength identified alongside the shadow side that holds you back. Real growth insight, not just flattery." },
                { icon: "🌈", title: "Personal Color Scheme", desc: "A three-color identity palette derived from your profile — primary, secondary, and accent — that visually represents your energy." },
              ].map(({ icon, title, desc }) => (
                <div key={title} style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                  <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>{icon}</div>
                  <div>
                    <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "12px", fontWeight: "700", color: "#fff", letterSpacing: "1px", marginBottom: "4px", textTransform: "uppercase" }}>{title}</div>
                    <div style={{ fontSize: "13px", color: MUTED, lineHeight: "1.6" }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeUp(0.15)} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Mock profile card */}
            <div style={{ background: CARD_BG, border: `1.5px solid ${ACCENT}50`, borderRadius: "16px", overflow: "hidden", boxShadow: `0 0 60px ${ACCENT}20` }}>
              <div style={{ background: "#1a1a2e", height: "160px", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${ACCENT}30, ${ACCENT2}20)` }} />
                <div style={{ fontSize: "64px", position: "relative", zIndex: 2, filter: "drop-shadow(0 0 20px rgba(127,90,240,0.6))" }}>🦸</div>
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "60px", background: `linear-gradient(to bottom, transparent, ${CARD_BG})` }} />
              </div>
              <div style={{ padding: "20px 24px" }}>
                <div style={{ display: "inline-flex", alignItems: "center", background: `${ACCENT}12`, border: `1px solid ${ACCENT}`, color: ACCENT, padding: "3px 10px", borderRadius: "3px", fontSize: "8px", fontFamily: "'Orbitron', monospace", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "8px" }}>The Strategist</div>
                <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "20px", fontWeight: "900", color: "#fff", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "4px" }}>Iron Architect</div>
                <div style={{ fontSize: "13px", color: MUTED, fontStyle: "italic", marginBottom: "16px" }}>"The system bends to those who understand it."</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
                  {["Analytical", "Visionary", "Decisive"].map(t => (
                    <span key={t} style={{ background: `${ACCENT}12`, border: `1px solid ${ACCENT}30`, color: MUTED, padding: "3px 10px", borderRadius: "20px", fontSize: "11px" }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                  <div style={{ background: "#0f3d25", border: "1px solid #16a34a40", borderRadius: "6px", padding: "10px 12px" }}>
                    <div style={{ color: "#16a34a", fontSize: "8px", letterSpacing: "2px", fontFamily: "'Orbitron', monospace", marginBottom: "4px" }}>SUPERPOWER</div>
                    <div style={{ fontSize: "12px", color: "#d1fae5" }}>Systems Thinking</div>
                  </div>
                  <div style={{ background: "#3d0f0f", border: "1px solid #dc262640", borderRadius: "6px", padding: "10px 12px" }}>
                    <div style={{ color: "#dc2626", fontSize: "8px", letterSpacing: "2px", fontFamily: "'Orbitron', monospace", marginBottom: "4px" }}>KRYPTONITE</div>
                    <div style={{ fontSize: "12px", color: "#fecaca" }}>Over-Planning</div>
                  </div>
                </div>
              </div>
              <div style={{ padding: "12px 24px", borderTop: `1px solid ${BORDER}`, display: "flex", gap: "6px" }}>
                <div style={{ flex: 1, height: "4px", borderRadius: "4px", background: ACCENT }} />
                <div style={{ flex: 1, height: "4px", borderRadius: "4px", background: "#2cb67d" }} />
                <div style={{ flex: 1, height: "4px", borderRadius: "4px", background: "#ff6b6b" }} />
              </div>
            </div>

            {/* Numerology mini cards */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
              {[
                { label: "Mulank", num: "7", sub: "The Seeker" },
                { label: "Expression", num: "11", sub: "Master Number" },
                { label: "Soul Urge", num: "3", sub: "The Creator" },
              ].map(({ label, num, sub }) => (
                <div key={label} style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "14px", textAlign: "center" }}>
                  <div style={{ fontSize: "8px", color: MUTED, letterSpacing: "1.5px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace", marginBottom: "6px" }}>{label}</div>
                  <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "24px", fontWeight: "900", color: ACCENT }}>{num}</div>
                  <div style={{ fontSize: "10px", color: MUTED, marginTop: "4px" }}>{sub}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </Section>

      <GlowLine />

      {/* ─── EVENTS ──────────────────────────────────────────────────────── */}
      <Section id="events" style={{ background: `${CARD_BG}80` }}>
        <motion.div {...fadeUp(0)} style={{ textAlign: "center", marginBottom: "64px" }}>
          <Tag>🎪 Event Ready</Tag>
          <H2>Built for Live Events<br />& Expo Booths</H2>
          <p style={{ color: MUTED, fontSize: "16px", maxWidth: "560px", margin: "0 auto", lineHeight: "1.7" }}>
            HeroScan was designed from day one for high-energy environments — fast, engaging, and shareable. Turn your booth into the highlight of the event.
          </p>
        </motion.div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "20px" }}>
          {[
            { icon: "⚡", title: "Under 3 Minutes", desc: "From first click to full superhero profile — fast enough for a busy expo floor." },
            { icon: "📲", title: "No App Required", desc: "Runs entirely in the browser. Visitors just scan a QR code and they're in." },
            { icon: "🎯", title: "Lead Capture Built In", desc: "Email collection built into the final step — every hero becomes a warm lead." },
            { icon: "📊", title: "Admin Dashboard", desc: "Real-time view of all visitors, profiles, and leads from a single admin panel." },
            { icon: "🖼", title: "Downloadable PDF", desc: "Every visitor leaves with a branded PDF of their hero card — a keepsake they'll share." },
            { icon: "🔁", title: "Infinitely Scalable", desc: "Handle hundreds of visitors simultaneously. Cloud-hosted, no local setup needed." },
          ].map(({ icon, title, desc }, i) => (
            <motion.div key={title} {...fadeUp(i * 0.07)} style={{
              background: CARD_BG, border: `1px solid ${BORDER}`,
              borderRadius: "12px", padding: "28px 24px",
            }}>
              <div style={{ fontSize: "28px", marginBottom: "14px" }}>{icon}</div>
              <div style={{ fontFamily: "'Orbitron', monospace", fontSize: "12px", fontWeight: "700", color: "#fff", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>{title}</div>
              <div style={{ fontSize: "13px", color: MUTED, lineHeight: "1.6" }}>{desc}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      <GlowLine />

      {/* ─── TECH STACK STRIP ────────────────────────────────────────────── */}
      <Section style={{ padding: "60px 24px" }}>
        <motion.div {...fadeUp(0)} style={{ textAlign: "center", marginBottom: "36px" }}>
          <div style={{ fontSize: "11px", color: MUTED, letterSpacing: "3px", textTransform: "uppercase", fontFamily: "'Orbitron', monospace" }}>Powered By</div>
        </motion.div>
        <motion.div {...fadeUp(0.1)} style={{ display: "flex", justifyContent: "center", gap: "40px", flexWrap: "wrap", alignItems: "center" }}>
          {[
            { label: "Anthropic Claude", sub: "Personality AI" },
            { label: "OpenAI GPT-4o", sub: "Avatar Generation" },
            { label: "React 19", sub: "Frontend" },
            { label: "FastAPI", sub: "Backend" },
            { label: "MongoDB Atlas", sub: "Database" },
            { label: "AWS S3", sub: "Media Storage" },
          ].map(({ label, sub }) => (
            <div key={label} style={{ textAlign: "center", padding: "16px 24px", border: `1px solid ${BORDER}`, borderRadius: "8px", minWidth: "120px" }}>
              <div style={{ fontSize: "13px", fontWeight: "700", color: "#fff", marginBottom: "4px" }}>{label}</div>
              <div style={{ fontSize: "10px", color: MUTED, letterSpacing: "1px" }}>{sub}</div>
            </div>
          ))}
        </motion.div>
      </Section>

      <GlowLine />

      {/* ─── CTA ─────────────────────────────────────────────────────────── */}
      <Section style={{ textAlign: "center" }}>
        <motion.div {...fadeUp(0)} style={{ position: "relative", background: CARD_BG, border: `1.5px solid ${ACCENT}50`, borderRadius: "24px", padding: "80px 40px", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "600px", height: "300px", background: `radial-gradient(ellipse, ${ACCENT}20, transparent 70%)`, pointerEvents: "none" }} />
          <div style={{ position: "relative", zIndex: 2 }}>
            <Tag>🚀 Get Started</Tag>
            <H2 style={{ fontSize: "clamp(28px, 5vw, 52px)" }}>Ready to Meet<br />Your Hero Self?</H2>
            <p style={{ color: MUTED, fontSize: "17px", lineHeight: "1.7", maxWidth: "480px", margin: "0 auto 40px" }}>
              Join hundreds of people who've already discovered their superhero identity. It's free, fast, and genuinely revealing.
            </p>
            <button
              onClick={handleCTA}
              style={{
                background: `linear-gradient(135deg, ${ACCENT}, #6644cc)`,
                color: "#fff", border: "none",
                padding: "18px 52px", borderRadius: "8px", cursor: "pointer",
                fontFamily: "'Orbitron', monospace", fontSize: "14px",
                fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase",
                boxShadow: `0 0 48px ${ACCENT}50`,
              }}
            >Begin Your Scan →</button>
          </div>
        </motion.div>
      </Section>

      {/* ─── FOOTER ──────────────────────────────────────────────────────── */}
      <footer style={{ background: CARD_BG, borderTop: `1px solid ${BORDER}`, padding: "40px 32px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <img src="/Scrobits_logo_black.png" alt="Scrobits" style={{ height: "22px", filter: "invert(1)", opacity: 0.7 }} />
            <span style={{ fontFamily: "'Orbitron', monospace", color: ACCENT, fontSize: "12px", fontWeight: "700", letterSpacing: "3px" }}>HEROSCAN</span>
          </div>
          <div style={{ fontSize: "12px", color: MUTED, textAlign: "center" }}>
            A product by <span style={{ color: ACCENT }}>Scrobits Technology</span> · contact@scrobits.com
          </div>
          <div style={{ fontSize: "12px", color: MUTED }}>
            <a href="https://www.scrobits.com" style={{ color: ACCENT, textDecoration: "none" }}>www.scrobits.com</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
